"use client"

// components/tracker/digest-next/digest-wheel-narrow.tsx
//
// The wheel below a 928px sheet (FE-DIGEST-1 Phase D4c2). Paper: the tablet
// block 2QMI-0 and its frames 2QF0-0, 2QGD-0, 2QHQ-0, 2QJ3-0, 2QC9-0, the
// quiet night in 2SKA-0 and the loading block in 2SC2-0 (drawn at 720), the
// mobile block 2QE2-0 and its frames 2QT5-0, 2QV2-0, 2QV3-0, 2QV4-0, 2QZS-0,
// the quiet night in 2RJF-0 and the loading block in 2RDO-0 (drawn at 358).
// The code's bands are tablet from 560 to 927 and mobile below 560.
//
// THE SAME MODEL AS DESKTOP. `buildWheelGroups` gives every chip line, item
// and empty state, and the opening type. The detail pieces (header, text,
// rows, stepper) are the desktop card's own, imported. Mounted only by
// `DigestWheelCard`, which holds the selection, so a resize across 928 in
// either direction keeps the selected type and the stepper's place.
//
// CHIPS ARE A REAL TAB WIDGET, as the desktop pills: one tab stop (roving
// tabindex), arrow keys, Home and End, selection following focus, no wrap,
// and the detail as the tab panel. Selection shows by text only (Oliver,
// 2026-10-04 10:23Z): dark semibold against #5F6368, no ring, no border.
// The first line is the model's short pill line on every chip, the selected
// one included (Oliver, 2026-10-03 11:08Z).
//
// THE ROW SCROLLS SIDEWAYS natively (no drag code, no visible scrollbar).
// The selected chip is brought fully into view inside the row only, by the
// row's own scrollLeft and never scrollIntoView, so the page never moves:
// on mount, on a change of selection, when a chip takes keyboard focus and
// when the row changes size (the edition strip's approach, D4c1 round 4).
//
// THE GRADIENT is the desktop card's `WheelGradient`, unchanged, in the
// panel. Paper's narrow crop (the layer's size, offset and rotation in the
// panel) is set by the CSS module on this block, over the component's
// desktop box. The still JPG stays beneath as the fallback.

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"

import { Ghost } from "@/components/tracker/ghost"
import { WheelGradient } from "@/components/tracker/digest-next/wheel-gradient"
import {
  Header,
  MOTION_EASE,
  MOTION_IN_MS,
  MOTION_OUT_MS,
  Paragraphs,
  Rows,
  STEP_NOUN,
  Stepper,
  WheelTypeIcon,
  sanitiseGhost,
  useReducedMotion,
  type WheelSelection,
} from "@/components/tracker/digest-next/digest-wheel-card"
import {
  WHEEL_ORDER,
  type WheelEmpty,
  type WheelGroup,
  type WheelItem,
  type WheelModel,
  type WheelTypeId,
} from "@/lib/digest/wheel-groups"
import styles from "@/components/tracker/digest-next/digest-next.module.css"

/** The row's inset at each end (Paper: 16 from the panel's edge), kept
 *  clear when a chip is brought into view. */
const ROW_INSET = 16

/** Scroll the row, and only the row, so `chip` is fully in view with the
 *  row's inset beside it. Does nothing when the row does not overflow or the
 *  chip is already in view. Instant, never smooth. */
function revealChip(row: HTMLElement | null, chip: HTMLElement | null) {
  if (!row || !chip) return
  if (row.scrollWidth <= row.clientWidth) return
  const r = row.getBoundingClientRect()
  const c = chip.getBoundingClientRect()
  if (c.left < r.left + ROW_INSET) row.scrollLeft += c.left - (r.left + ROW_INSET)
  else if (c.right > r.right - ROW_INSET) row.scrollLeft += c.right - (r.right - ROW_INSET)
}

/** Paper's narrow headline: 24/32 at every length, -0.02em. */
function NarrowHeadline({ text }: { text: string }) {
  return <h3 className={`${styles.dnWheelHeadline} ${styles.dnWheelNarrowHeadline}`}>{text}</h3>
}

function NarrowItemDetail({
  group,
  item,
  index,
  count,
  grouped,
  mobile,
  onStep,
}: {
  group: WheelGroup
  item: WheelItem
  index: number
  count: number
  grouped: boolean
  /** Below a 560 sheet: the stepper's row comes before the source's. */
  mobile: boolean
  onStep: (next: number) => void
}) {
  const stepper =
    count > 1 ? (
      <Stepper index={index} count={count} noun={STEP_NOUN[group.type]} onStep={onStep} />
    ) : null
  return (
    <>
      <Header identity={item.identity} type={group.type} name={item.name} subLine={item.subLine} />
      {item.date ? (
        <div className={styles.dnWheelDateRow}>
          <span className={styles.dnWheelDateTile}>
            <WheelTypeIcon type={group.type} size={18} />
          </span>
          <span className={styles.dnWheelDate}>{item.date}</span>
        </div>
      ) : null}
      <p className={styles.dnWheelTypeLine}>{group.name}</p>
      <NarrowHeadline text={item.headline} />
      <Paragraphs paragraphs={item.body} />
      <Rows rows={item.rows} />
      {/* A grouped count has no single source, so the footer is left out,
          as on desktop. Tablet: source left, stepper right (2QMK-0).
          Mobile: the stepper's row above the source's (2QZW-0, 2QZU-0).
          The DOM follows the drawn order at each width, so focus order
          matches what is seen (round 2). */}
      {grouped ? null : (
        <div className={styles.dnWheelNarrowFoot}>
          {mobile ? stepper : null}
          <div className={styles.dnWheelNarrowSourceRow}>
            {item.source ? (
              <a
                className={styles.dnWheelSource}
                href={item.source.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.source.label} ↗<span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : (
              <span className={styles.dnWheelNoSource}>Source link not available</span>
            )}
          </div>
          {mobile ? null : stepper}
        </div>
      )}
    </>
  )
}

function NarrowEmptyDetail({ group, empty }: { group: WheelGroup; empty: WheelEmpty }) {
  return (
    <>
      <Header identity={empty.identity} type={group.type} name={empty.name} subLine={empty.subLine} />
      {empty.showTypeLine ? <p className={styles.dnWheelTypeLine}>{group.name}</p> : null}
      <NarrowHeadline text={empty.headline} />
      <Paragraphs paragraphs={[empty.body]} />
      <Rows rows={empty.rows} />
    </>
  )
}

export function DigestWheelNarrow({
  model,
  isBackIssue,
  mobile,
  selected,
  setSelected,
  index,
  setIndex,
}: {
  model: WheelModel
  isBackIssue: boolean
  /** Below a 560 sheet (from use-sheet-band.ts, the module's line). */
  mobile: boolean
} & WheelSelection) {
  const baseId = useId()
  const [announce, setAnnounce] = useState("")
  const reduced = useReducedMotion()
  const rowRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<Partial<Record<WheelTypeId, HTMLButtonElement | null>>>({})
  const focusPending = useRef(false)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const ghostHostRef = useRef<HTMLDivElement | null>(null)
  const ghost = useRef<{ el: HTMLElement; anim: Animation } | null>(null)
  const incoming = useRef<Animation | null>(null)
  const pendingIn = useRef(false)

  const selectedPos = WHEEL_ORDER.indexOf(selected)
  const group = model.groups.find((g) => g.type === selected) ?? model.groups[0]
  const items = group.grouped ? [group.grouped] : group.items
  const safeIndex = Math.min(index, Math.max(0, items.length - 1))
  const item = items[safeIndex] ?? null

  const panelHasFocusable =
    item !== null &&
    (item.source !== null || item.rows.some((r) => r.href) || items.length > 1)

  const tabId = (t: WheelTypeId) => `${baseId}-tab-${t}`
  const panelId = `${baseId}-panel`
  const headingId = `${baseId}-heading`

  /* The detail transition, as the desktop card's (house crossfade rule): a
   * still, inert copy of the outgoing detail fades out over 240ms above the
   * incoming one, which fades up over 400ms with a 4px rise. Retargets,
   * never queues. Nothing under reduced motion. */
  const beginDetailChange = () => {
    const inner = innerRef.current
    const host = ghostHostRef.current
    if (ghost.current) {
      ghost.current.anim.cancel()
      ghost.current.el.remove()
      ghost.current = null
    }
    if (reduced || !inner || !host || typeof inner.animate !== "function") return
    const from = Number.parseFloat(getComputedStyle(inner).opacity) || 1
    const copy = inner.cloneNode(true) as HTMLElement
    sanitiseGhost(copy)
    host.appendChild(copy)
    const anim = copy.animate([{ opacity: from }, { opacity: 0 }], {
      duration: MOTION_OUT_MS,
      easing: MOTION_EASE,
      fill: "forwards",
    })
    ghost.current = { el: copy, anim }
    anim.finished.then(
      () => {
        if (ghost.current?.anim === anim) {
          copy.remove()
          ghost.current = null
        }
      },
      () => undefined,
    )
    pendingIn.current = true
  }

  useLayoutEffect(() => {
    if (!pendingIn.current) return
    pendingIn.current = false
    incoming.current?.cancel()
    const inner = innerRef.current
    if (!inner || reduced || typeof inner.animate !== "function") return
    incoming.current = inner.animate(
      [
        { opacity: 0, transform: "translateY(4px)" },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: MOTION_IN_MS, easing: MOTION_EASE },
    )
  }, [selected, index, reduced])

  useEffect(() => {
    if (!reduced) return
    incoming.current?.cancel()
    if (ghost.current) {
      ghost.current.anim.cancel()
      ghost.current.el.remove()
      ghost.current = null
    }
  }, [reduced])

  // The selected chip in view: on mount and on every change of selection.
  useLayoutEffect(() => {
    revealChip(rowRef.current, tabRefs.current[selected] ?? null)
  }, [selected])

  // And again when the row changes size (sidebar, rotation). A
  // ResizeObserver never fires on scroll, so the member's own scrolling of
  // the row is never undone.
  useEffect(() => {
    const row = rowRef.current
    if (!row || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() =>
      revealChip(row, row.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')),
    )
    observer.observe(row)
    return () => observer.disconnect()
  }, [])

  const select = (t: WheelTypeId, focus: boolean) => {
    if (t === selected) return
    beginDetailChange()
    setSelected(t)
    setIndex(0)
    setAnnounce("")
    focusPending.current = focus
  }

  // Focus follows the selection once the chip is rendered. The row is
  // scrolled by revealChip, so the page is not moved by the focus.
  useEffect(() => {
    if (!focusPending.current) return
    focusPending.current = false
    tabRefs.current[selected]?.focus({ preventScroll: true })
  }, [selected])

  const onChipClick = (t: WheelTypeId, el: HTMLButtonElement) => {
    if (t === selected) return
    select(t, false)
    // Safari does not focus a clicked button: the live region names the type.
    if (typeof document !== "undefined" && document.activeElement !== el) {
      setAnnounce(model.groups.find((g) => g.type === t)?.name ?? "")
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    let next: number | null = null
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = selectedPos - 1
    else if (e.key === "ArrowDown" || e.key === "ArrowRight") next = selectedPos + 1
    else if (e.key === "Home") next = 0
    else if (e.key === "End") next = WHEEL_ORDER.length - 1
    if (next === null) return
    e.preventDefault()
    // The row has ends: it does not wrap, as the wheel.
    const clamped = Math.max(0, Math.min(WHEEL_ORDER.length - 1, next))
    select(WHEEL_ORDER[clamped], true)
  }

  const step = (n: number) => {
    beginDetailChange()
    setIndex(n)
    setAnnounce(`${n + 1} of ${items.length}`)
  }

  return (
    <section className={styles.dnWheelNarrowBlock} aria-labelledby={headingId}>
      {/* The same heading as the desktop card, so the outline is the same
          at every width. */}
      <h2 id={headingId} className="sr-only">
        {isBackIssue ? "Updates by type" : "Tonight’s updates by type"}
      </h2>
      <div className={styles.dnWheelNarrowPanel} data-wheel-type={selected}>
        <WheelGradient type={selected} reduced={reduced} />
        <div
          ref={rowRef}
          className={styles.dnWheelChipRow}
          role="tablist"
          aria-orientation="horizontal"
          aria-label="Event types"
        >
          <div className={styles.dnWheelChipTrack}>
            {WHEEL_ORDER.map((t, i) => {
              const g = model.groups.find((x) => x.type === t)!
              const active = t === selected
              return (
                <button
                  key={t}
                  ref={(el) => {
                    tabRefs.current[t] = el
                  }}
                  id={tabId(t)}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-posinset={i + 1}
                  aria-setsize={WHEEL_ORDER.length}
                  aria-controls={panelId}
                  aria-label={`${g.name}, ${g.pillLine}`}
                  tabIndex={active ? 0 : -1}
                  className={`${styles.dnWheelChipTab} ${active ? styles.dnWheelChipTabActive : ""}`}
                  onClick={(e) => onChipClick(t, e.currentTarget)}
                  onKeyDown={onKeyDown}
                  onFocus={(e) => revealChip(rowRef.current, e.currentTarget)}
                >
                  <span className={styles.dnWheelChipLine}>{g.pillLine}</span>
                  <span className={styles.dnWheelChipName}>{g.name}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
      <div className={styles.dnWheelNarrowBody}>
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={tabId(selected)}
          // A panel with no link or button in it takes a tab stop itself.
          tabIndex={panelHasFocusable ? undefined : 0}
          className={styles.dnWheelNarrowDetail}
        >
          <div ref={innerRef} className={styles.dnWheelNarrowInner}>
            {item ? (
              // No key per item: the stepper stays mounted across a step, so
              // the focused button is kept.
              <NarrowItemDetail
                group={group}
                item={item}
                index={safeIndex}
                count={group.grouped ? 1 : items.length}
                grouped={group.grouped !== null}
                mobile={mobile}
                onStep={step}
              />
            ) : group.empty ? (
              <NarrowEmptyDetail key={`${group.type}-empty`} group={group} empty={group.empty} />
            ) : null}
          </div>
        </div>
        {/* The outgoing detail's still copy while it fades. Empty to React. */}
        <div ref={ghostHostRef} className={styles.dnWheelNarrowGhostHost} aria-hidden="true" />
      </div>
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
    </section>
  )
}

/** The loading block (Paper 2SH9-0 at 720, 2SMV-0 at 358): a plain white
 *  panel with four ghost chips, then the ghost detail. Shown by the CSS
 *  module below a 928 sheet, in place of the desktop ghost card. No gradient
 *  and no renderer. Every shape is aria-hidden; the page's one status line
 *  says it is loading. */
const GHOST_CHIPS_TABLET = [126, 126, 126, 126] as const
const GHOST_CHIPS_MOBILE = [132, 137, 124, 125] as const

export function WheelNarrowLoading() {
  const bar = (w: number | string) => <Ghost.Bar w={w} h={8} style={{ borderRadius: 4 }} />
  return (
    <div className={styles.dnWheelNarrowGhost} aria-hidden="true">
      <div className={styles.dnWheelNarrowGhostPanel}>
        {/* Paper draws 126-wide ghosts at 720 and four widths at 358: the
            module shows the band's set. */}
        <div className={`${styles.dnWheelChipTrack} ${styles.dnWheelGhostTablet}`}>
          {GHOST_CHIPS_TABLET.map((w, i) => (
            <Ghost.Block key={i} w={w} h={44} r={12} />
          ))}
        </div>
        <div className={`${styles.dnWheelChipTrack} ${styles.dnWheelGhostMobile}`}>
          {GHOST_CHIPS_MOBILE.map((w, i) => (
            <Ghost.Block key={i} w={w} h={44} r={12} />
          ))}
        </div>
      </div>
      <div className={styles.dnWheelNarrowGhostDetail}>
        <div className={styles.dnWheelGhostHeader}>
          <Ghost.Block w={56} h={56} r={999} />
          <div className={styles.dnWheelGhostStack}>
            <Ghost.Block w={140} h={16} r={4} />
            {bar(200)}
          </div>
        </div>
        <Ghost.Block w={120} h={16} r={4} />
        {/* The headline: one 32px line at 720, two 24px lines at 358. */}
        <div className={`${styles.dnWheelGhostStack} ${styles.dnWheelGhostTablet}`}>
          <Ghost.Block w="90%" h={32} r={4} />
        </div>
        <div className={`${styles.dnWheelGhostStack} ${styles.dnWheelGhostMobile}`}>
          <Ghost.Block w="90%" h={24} r={4} />
          <Ghost.Block w="60%" h={24} r={4} />
        </div>
        <div className={styles.dnWheelGhostStack}>
          {bar("100%")}
          {bar("92%")}
          {bar("64%")}
        </div>
        <div className={styles.dnWheelGhostStack}>
          {bar("100%")}
          {bar("100%")}
        </div>
        {bar(160)}
      </div>
    </div>
  )
}
